<template>
  <v-app>
    <icon-font-wrapper>
      <!-- Top navbar -->
      <v-toolbar id="sb-header" app dense clipped-left>
        <a :href="`${SbBaseUrl}`" class="sphere-logo">
          <img
            :src="`${SbBaseUrl}/images/SPHEREboard_Logo_2024_white.svg`"
            height="40"
            alt="SPHEREboard"
          />
        </a>
        <v-toolbar-title id="MainHeaderTitle">
          <span>{{ $route.meta.title }}</span>
          <span v-if="!!subHeadingText"> : {{ subHeadingText }}</span>
          <span v-else-if="!!$route.meta.subHeader">
            : {{ $route.meta.subHeader }}
          </span>
        </v-toolbar-title>
        <v-spacer />
        <global-search-control @assetselected="getSelectedAsset" />
        <v-btn
          id="user-guide"
          :href="`https://docs.sphereco.com/sphereboard-${quickHelpVersion}-user-guides`"
          class="header-icon-button mr-0"
          target="_blank"
          icon
          dark
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attr }">
              <v-icon class="material-icons-outlined" v-bind="attr" v-on="on">
                help_outline
              </v-icon>
            </template>
            <span>Open the user guide</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          id="request-support"
          href="https://customer.sphereco.com/"
          class="header-icon-button"
          target="_blank"
          icon
          dark
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attr }">
              <v-icon class="material-icons-outlined" v-bind="attr" v-on="on">
                support_agent
              </v-icon>
            </template>
            <span>Request Support</span>
          </v-tooltip>
        </v-btn>
        <span id="currentUser">{{ currentUser }}</span>
        <v-menu
          v-if="showLogout"
          v-model="isExpanded"
          transition="scale-transition"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <v-icon color="white" v-on="on">
              {{ isExpanded ? 'expand_less' : 'expand_more' }}
            </v-icon>
          </template>
          <v-btn class="sb-logout-btn" data-cy="sb-logout-btn" @click="logout"
            ><v-icon small data-cy="sb-logout-icon" class="mr-2">
              logout
            </v-icon>
            Log out
          </v-btn>
        </v-menu>
      </v-toolbar>
      <!-- Left navigation drawer -->
      <main-layout-sidebar
        v-if="!hideSidebar"
        ref="sidebar"
        :width="navDrawerWidth"
      />

      <!-- license alert -->
      <license-alert v-if="displayLicenseWarning" />

      <!-- Page content -->
      <v-content :style="`padding-left: ${navDrawerWidth}px;`">
        <v-container fluid class="pa-2">
          <the-offline-alert />
          <status-display-bar :error="errorMessage" />
          <v-dialog v-model="showIdleAlert" max-width="500px" persistent>
            <v-card>
              <v-card-text>
                <v-layout>
                  <v-flex>
                    Session has been idle for {{ appIdleAlertTime }} minutes.
                    Please click on OK to continue.
                  </v-flex>
                </v-layout>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <template>
                  <v-btn
                    color="success"
                    class="sb_btn_solid"
                    @click="closeIdleWarning"
                  >
                    OK
                  </v-btn>
                </template>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <div :class="wrapperClass">
            <!-- Router content -->
            <router-view />
          </div>
        </v-container>
      </v-content>

      <!-- Page footer -->
      <the-footer />
    </icon-font-wrapper>
  </v-app>
</template>

<script>
import TheOfflineAlert from '../components/TheOfflineAlert.vue';
import TheFooter from '../components/TheFooter.vue';
import StatusDisplayBar from '../components/StatusDisplayBar.vue';
import IconFontWrapper from '../components/IconFontWrapper.vue';
import MainLayoutSidebar from './MainLayoutSidebar.vue';
import GlobalSearchControl from '../components/GlobalSearchControl.vue';
import { fetch } from '../utility/fetch.js';
import LicenseAlert from '../components/LicenseAlert.vue';
import Cookies from 'js-cookie';

export default {
  name: 'MainLayout',
  components: {
    TheOfflineAlert,
    TheFooter,
    StatusDisplayBar,
    IconFontWrapper,
    MainLayoutSidebar,
    GlobalSearchControl,
    LicenseAlert,
  },
  props: {
    title: {
      type: String,
      default: 'Layout Title',
    },
    isHomePage: {
      type: Boolean,
    },

    wrapperClass: { type: String, default: '' },
  },
  data() {
    return {
      menuHeading: '',
      menuToggleClass: false,
      subHeadingText: '',
      errorMessage: undefined,
      currentUser: 'Guest',
      navDrawerWidth: 60,
      quickHelpVersion: '6.0',
      assetType: '',
      showIdleAlert: false,
      appIdleAlertTime: 0,
      appIdleTimeOut: 0,
      hideSidebar: false,
      isExpanded: false,
      showLogout: true,
    };
  },
  computed: {
    displayLicenseWarning() {
      const meta = this.$route.meta;
      return !(
        meta.title == 'Asset Review Module' &&
        meta.hasOwnProperty('subHeader') &&
        meta.subHeader == 'Asset Review'
      );
    },
  },
  async created() {
    const response = await fetch('/License/Get');
    if (response) {
      Cookies.set('license-json', JSON.stringify(response), { expires: 1 });
    }
    await this.$store.dispatch('LOAD_SITE_CONFIG_VALUES');
    // Fetch latest IdleTimeOut and IdleAlertTime values
    if (!this.$store.state.columnConfig.siteConfigFetchedFresh) {
      await this.$store.dispatch('LOAD_IDLE_CONFIG_VALUES');
    }
    // Access configured Idle AletTime and Timeout values
    const timeoutConfig =
      this.$store.state.columnConfig.siteConfigValues['IdleTimeOut'];
    this.appIdleTimeOut = parseInt(timeoutConfig);
    const alertConfig =
      this.$store.state.columnConfig.siteConfigValues['IdleAlertTime'];
    this.appIdleAlertTime = parseInt(alertConfig);
    this.sphereAuthURL = await fetch(
      '/Home/GetAppConfig?v1=SphereAuth&v2=LoginURL'
    );
  },
  //eslint-disable-next-line complexity
  mounted() {
    localStorage.setItem('lastActivity', Date.now());
    this.$store.dispatch('FETCH_CURRENT_USER');
    let userUserName = document.getElementById('view-data:UserName');
    let userFullName = document.getElementById('view-data:User');
    if (userFullName !== null) {
      if (userFullName.value.includes(',')) {
        this.currentUser = userFullName.value.split(',').reverse().join(' ');
      } else {
        this.currentUser = userUserName.value;
      }
    }

    this.menuHeading = this.$route.meta.title;
    let emailFlag = this.$store.state.campaignDetails
      ? this.$store.state.campaignDetails.emailFlag
      : false;
    if (this.$route.meta.email != undefined || emailFlag) {
      this.hideSidebar = true;
    }
    let moduleDetails = document.getElementById('view-data:ModuleDetails')
      ? JSON.parse(document.getElementById('view-data:ModuleDetails').value)
      : [];

    this.$store.dispatch('FETCH_USER_CARDS').catch((error) => {
      this.errorMessage = `Unable get user cards: ${error.message}`;
      console.error(error);
    });
    this.$root.$on('change-sub-header', (item) => {
      this.subHeadingText = item;
    });

    const versionInputElem = document.getElementById('sb-version');
    if (versionInputElem) {
      let versionNumbers = versionInputElem.value.split('.');
      this.quickHelpVersion = versionNumbers[0] + '-' + versionNumbers[1];
    }
    window.addEventListener('storage', (event) => {
      if (event.key === 'lastActivity') {
        this.showIdleAlert = false;
      }
      if (event.key === 'logout-event') {
        window.location.href = this.SbBaseUrl + '/Logout';
      }
    });
    this.handleSessionIdleTimeout();
  },
  methods: {
    // Handle session Timeout
    handleSessionIdleTimeout() {
      // Increment the idle time counter every minute.
      setInterval(() => {
        this.incrementTimer();
      }, 60000);

      // Heartbeat: keep the SPHERE.Auth token alive while the user is active.
      // Fires every 4 minutes — within SPHERE.Auth's 5-minute auto-refresh window.
      // Stops once the idle alert is showing (logout is imminent; no point refreshing).
      setInterval(() => {
        const lastActivity =
          Number(localStorage.getItem('lastActivity')) || Date.now();
        const idleMinutes = Math.floor((Date.now() - lastActivity) / 60000);
        const isActive =
          this.appIdleTimeOut === 0 || idleMinutes < this.appIdleTimeOut;
        if (isActive && !this.showIdleAlert) {
          fetch('/Home/GetAppConfig?v1=SphereAuth&v2=LoginURL').catch(() => {});
        }
      }, 4 * 60000);

      // When the tab becomes visible again (after sleep / background), immediately
      // fire a heartbeat so SPHERE.Auth can refresh the token before the next
      // real request hits a 401.
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && !this.showIdleAlert) {
          const lastActivity =
            Number(localStorage.getItem('lastActivity')) || Date.now();
          const idleMinutes = Math.floor((Date.now() - lastActivity) / 60000);
          const isActive =
            this.appIdleTimeOut === 0 || idleMinutes < this.appIdleTimeOut;
          if (isActive) {
            fetch('/Home/GetAppConfig?v1=SphereAuth&v2=LoginURL').catch(
              () => {}
            );
          }
        }
      });

      // Reset the idle timer on mouse movement.
      this.$el.addEventListener('mousemove', this.resetTimer);
      this.$el.addEventListener('keypress', this.resetTimer);
    },
    async incrementTimer() {
      const lastActivity =
        Number(localStorage.getItem('lastActivity')) || Date.now();
      const idleMinutes = Math.floor((Date.now() - lastActivity) / 60000);
      if (
        this.appIdleAlertTime > 0 &&
        idleMinutes >= this.appIdleAlertTime &&
        !this.showIdleAlert
      ) {
        this.showIdleAlert = true;
      }
      if (this.appIdleTimeOut > 0 && idleMinutes >= this.appIdleTimeOut) {
        await this.logout();
      }
    },
    resetTimer() {
      if (!this.showIdleAlert) {
        localStorage.setItem('lastActivity', Date.now());
      }
    },
    closeIdleWarning() {
      this.showIdleAlert = false;
      localStorage.setItem('lastActivity', Date.now());
    },
    getSelectedAsset(assetType) {
      this.assetType = assetType;
    },

    async logout() {
      localStorage.setItem('logout-event', Date.now());
      window.location.href = this.SbBaseUrl + '/Logout';
    },
  },
};
</script>

<style>
@import '../static/css/SphereGlobal.css';
@import '../static/css/VuetifyOverrides.css';
@import '../static/css/Components/MainLayout.css';
</style>

<style scoped>
.v-menu__content {
  box-shadow: none;
  position: fixed !important;
}
</style>
